import { type AppError, ErrorCode, FatalError } from '@imprivata-cloud/authn';
import type { NOTIF_BANNER_TYPE } from '@imprivata-cloud/components';
import {
  CoreEventTypes,
  type FaceCeremony,
  useEmitters,
} from '@imprivata-cloud/core';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { resolveCameraNotification } from '@/errorHandler/camera';
import {
  DISPLAY_BANNER_TIME,
  IGNORED_ERROR_CODES,
  IGNORED_ERROR_MESSAGES,
  mapErrorToStatus,
  toDisplayStatus,
} from '@/utils/bannerUtils';

// The banner's presentation is a projection of where the failure happened, which
// only the caller dispatching the event knows (see useEventHandler): `ceremony`
// selects the camera copy ("enroll your face" vs "for face authentication"), and
// `isFaceScreen` says the face-capture screen is on view, where the banner is
// repositioned over the video, non-dismissible, and kept through interaction.
// Both are required rather than defaulted: a default is indistinguishable at the
// callsite from a deliberate choice, and the wrong ceremony misnames the failure.
export type BannerContext = {
  ceremony: FaceCeremony;
  isFaceScreen: boolean;
};

export function useBanner() {
  const { t } = useTranslation();
  const { emitUIEvent } = useEmitters();

  // Severity of the banner currently on screen (undefined when none). The banner
  // itself is rendered by the shared NotificationSystem, but the enrollment header
  // also swaps its central glyph to a red/orange face for an error/warning, so the
  // level is surfaced here for that projection (see EnrollmentHeader).
  const [bannerStatus, setBannerStatus] = useState<
    NOTIF_BANNER_TYPE | undefined
  >(undefined);

  const clearBanner = useCallback(() => {
    emitUIEvent(CoreEventTypes.CLEAR);
    setBannerStatus(undefined);
  }, [emitUIEvent]);

  const displayBanner = useCallback(
    (err: AppError, { ceremony, isFaceScreen }: BannerContext) => {
      const noBanner =
        (err instanceof FatalError &&
          err.code !== ErrorCode.INVALID_TENANT_ID) ||
        (err.code && IGNORED_ERROR_CODES.includes(err.code)) ||
        (err.message && IGNORED_ERROR_MESSAGES.includes(err.message));

      if (noBanner) {
        return;
      }

      // The true severity drives the enrollment header's glyph (red / orange
      // face); the banner itself is shown one notch softer — see toDisplayStatus.
      const status = toDisplayStatus(mapErrorToStatus(err));
      setBannerStatus(status);
      // On the face-capture screen the banner is one thing — repositioned over
      // the video, non-dismissible, and kept through interaction — so the flags
      // move together as a single presentation rather than separate knobs. Every
      // other banner also closes itself after DISPLAY_BANNER_TIME.
      const presentation = isFaceScreen
        ? {
            overrideClasses: 'authn-layout-face-banner',
            hideCloseBtn: true,
            persistOnInteraction: true,
          }
        : {
            overrideClasses: undefined,
            hideCloseBtn: false,
            persistOnInteraction: false,
            closeTimeInSec: DISPLAY_BANNER_TIME,
          };
      // Camera copy is owned by core's notification map, because a blocked camera
      // reads differently while enrolling than while authenticating — a pairing
      // the `errors.<code>` convention below cannot express.
      const cameraNotification = resolveCameraNotification(
        t,
        ceremony,
        err.code,
      );
      if (cameraNotification) {
        emitUIEvent(CoreEventTypes.UI_NOTIFY, {
          level: status,
          title: cameraNotification.title,
          message: cameraNotification.message,
          ...presentation,
        });
        return;
      }

      const title = err.code
        ? t(
            `errors.${err.code}`,
            err.params?.username
              ? { username: err.params.username }
              : undefined,
          )
        : t(err.message ?? '');

      const messageKey = `errors.${err.code}-sub-message`;
      const message = t(messageKey);

      emitUIEvent(CoreEventTypes.UI_NOTIFY, {
        level: status,
        title,
        message: message !== messageKey ? message : undefined,
        ...presentation,
      });
    },
    [t, emitUIEvent],
  );

  return { displayBanner, clearBanner, bannerStatus };
}
